import { Card, Icon, type IconTypes } from '@ics-portal/guardian';
import { type ReactNode } from 'react';
import { SurveyStatus } from '@gql/graphql';
import { getPssStatusPresentation } from '@pages/project/pss/_utils/get-pss-status-presentation';
import { capitalizeString } from '@utils/stringFormatUtils';
import { useSummaryCards } from '@pages/project/summary/_utils/contexts/SummaryCardsContext';
import { SummaryCardTitle } from './components/SummaryCardTitle';
import s from './SummaryCards.module.scss';
import { SummaryCardContent } from './components/SummaryCardContent';

interface Props {
    pid: string;
}

function StatusComponent(status?: SurveyStatus): ReactNode {
    const statusPresentation = getPssStatusPresentation(status);
    const getWrapper = (content: ReactNode): ReactNode => (
        <div className="flex gap-2">{content}</div>
    );
    const getContentWrapper = (content: string): ReactNode => (
        <span className="flex align-items-center text-white">{content}</span>
    );
    const getIconWrapper = (
        icon: IconTypes,
        className: string = status ? s.statusIcon : s.defaultIcon,
    ): ReactNode => <Icon name={icon} className={className} />;
    const iconClassName =
        status === SurveyStatus.Completed
            ? s.successIcon
            : status === SurveyStatus.Declined
              ? s.errorIcon
              : undefined;

    return getWrapper(
        <>
            {getIconWrapper(statusPresentation.icon, iconClassName)}
            {getContentWrapper(statusPresentation.label)}
        </>,
    );
}

export function PsspClassificationCard({ pid }: Props): ReactNode {
    const { data } = useSummaryCards();
    const survey = data?.survey;

    return (
        <Card
            title={
                <SummaryCardTitle
                    icon="category"
                    title="PSSP Classification"
                    iconClassName={s.psspClassificationIcon}
                />
            }
            cardClassName={s.card}
            foldable={false}
        >
            <SummaryCardContent
                content={
                    <h2 className="text-white">
                        {survey
                            ? capitalizeString(survey.classificationClassName)
                            : null}
                    </h2>
                }
                footer={StatusComponent(survey?.status)}
                to={`/projects/${pid}/pss`}
            />
        </Card>
    );
}
